import memoize from 'lodash/memoize';

import { EmojiNodeDataProvider } from '@atlaskit/editor-plugins/emoji';
import type { EmojiResourceConfig, default as EmojiResource } from '@atlaskit/emoji/emoji-resource';
import type { EmojiProvider } from '@atlaskit/emoji/types';
import type { EmojiLoadSuccessCallback, EmojiLoadFailCallback } from '@atlaskit/emoji/emoji-utils';

import { expValEquals } from '@confluence/feature-experiments';
import { getLogger } from '@confluence/logger';

import { OnDemandEmojiResource } from './OnDemandEmojiResource.ts';

export type EmojiSSRConfig = Pick<EmojiResourceConfig, 'optimisticImageApi'>;

type MemoizedFunction = ReturnType<typeof memoize>;

export type ConfigOptions = {
	cloudId?: string | null;
	userId?: string | null;
	disableUpload?: boolean;
	emojiSSRConfig?: EmojiSSRConfig;
	/**
	 * This option will force load site emoji resource
	 * regardless current user's auth status.
	 */
	allowAnonymousAccess?: boolean;
	onEmojiLoadSuccess?: EmojiLoadSuccessCallback;
	onEmojiLoadFail?: EmojiLoadFailCallback;
};
const logger = getLogger('emoji-provider');

export const BASE_URL = '/gateway/api/emoji';

export const getEmojiConfig = (
	cloudId?: string,
	userId?: string,
	disableUpload?: boolean,
	emojiSSRConfig?: EmojiSSRConfig,
	allowAnonymousAccess?: boolean,
	emojiLoadCallbacks?: {
		onEmojiLoadSuccess?: EmojiLoadSuccessCallback;
		onEmojiLoadFail?: EmojiLoadFailCallback;
	},
): EmojiResourceConfig => {
	const currentUser = userId ? { id: userId } : undefined;

	const config = {
		providers: [
			{
				url: `${BASE_URL}/standard`,
			},
			{
				url: `${BASE_URL}/atlassian`,
			},
		],
		allowUpload: !!userId && !disableUpload,
		currentUser,
		...emojiLoadCallbacks,
	};

	if (cloudId && (userId || allowAnonymousAccess)) {
		config.providers.push({
			url: `${BASE_URL}/${cloudId}/site`,
		});
	}

	if (emojiSSRConfig) {
		return { ...config, ...emojiSSRConfig };
	}

	return config;
};

type EmojiResourceConstructor = typeof EmojiResource;

export const resolverOfMemoize = (_EmojiResource: any, extraConfig?: ConfigOptions) => {
	const cloudId = extraConfig?.cloudId ?? '';
	const userId = extraConfig?.userId ?? '';
	const allowAnonymousAccess = extraConfig?.allowAnonymousAccess ?? false;
	const emojiSSRConfig = extraConfig?.emojiSSRConfig ?? undefined;
	// For signed-in users, allowAnonymousAccess does not change the provider config. Sharing the
	// resource lets uploads propagate immediately between editor, title, and reaction pickers.
	const allowAnonymousAccessCacheKey = userId ? false : allowAnonymousAccess;
	return `${cloudId}_${userId}_${allowAnonymousAccessCacheKey}_${emojiSSRConfig ? 'withSSR' : 'noSSR'}`;
};

export const getEmojiResource: ((
	EmojiResource: EmojiResourceConstructor,
	extraConfig?: ConfigOptions,
) => EmojiResource) &
	MemoizedFunction = memoize(
	(EmojiResource: EmojiResourceConstructor, extraConfig?: ConfigOptions): EmojiResource => {
		const cloudId = extraConfig?.cloudId ?? '';
		const userId = extraConfig?.userId ?? undefined;
		const disableUpload = extraConfig?.disableUpload;
		const emojiSSRConfig = extraConfig?.emojiSSRConfig ?? undefined;
		const allowAnonymousAccess = extraConfig?.allowAnonymousAccess ?? undefined;
		const emojiConfig = getEmojiConfig(
			cloudId,
			userId,
			disableUpload,
			emojiSSRConfig,
			allowAnonymousAccess,
		);

		if (
			cloudId &&
			userId &&
			expValEquals('confluence_on_demand_emoji_repository', 'isEnabled', true)
		) {
			return new OnDemandEmojiResource({
				...emojiConfig,
				options: { onlyFetchOnDemand: true },
				singleEmojiApi: {
					getUrl: ({ id, shortName }) =>
						`${BASE_URL}/${cloudId}/${encodeURIComponent(id ?? shortName)}`,
				},
			});
		}

		return new EmojiResource(emojiConfig);
	},
	resolverOfMemoize,
);

export const getEmojiProvider: ((
	EmojiResource: EmojiResourceConstructor,
	extraConfig?: ConfigOptions,
) => Promise<EmojiProvider>) &
	MemoizedFunction = memoize(
	(
		EmojiResource: EmojiResourceConstructor,
		extraConfig?: ConfigOptions,
	): Promise<EmojiProvider> => {
		try {
			const emojiResource = getEmojiResource(EmojiResource, extraConfig);

			return emojiResource.getEmojiProvider();
		} catch (err) {
			/* istanbul ignore next */
			if (process.env.NODE_ENV !== 'production') {
				logger.error`Error creating emoji resource ${err}`;
			}

			return Promise.resolve(undefined as any);
		}
	},
	resolverOfMemoize,
);

export const getEmojiNodeDataProvider: ((
	EmojiResource: EmojiResourceConstructor,
	extraConfig?: ConfigOptions,
) => EmojiNodeDataProvider | undefined) &
	MemoizedFunction = memoize(
	(
		EmojiResource: EmojiResourceConstructor,
		extraConfig?: ConfigOptions,
	): EmojiNodeDataProvider | undefined => {
		try {
			const emojiResource = getEmojiResource(EmojiResource, extraConfig);

			return new EmojiNodeDataProvider(emojiResource);
		} catch (err) {
			/* istanbul ignore next */
			if (process.env.NODE_ENV !== 'production') {
				logger.error`Error creating emoji resource ${err}`;
			}

			return undefined;
		}
	},
	resolverOfMemoize,
);
